import { Box, VStack, HStack, Skeleton, SkeletonCircle, Text } from '@chakra-ui/react';
import ColoredDotsDescription from '../../ColoredDotsDescription';

type Props = {
  title?: string;
  greenScoreDescription?: string;
  yellowScoreDescription?: string;
  redScoreDescription?: string;
  titleShown?: boolean;
};

const CategoryCardSkeleton = ({
  textWidth = '52px',
  flip = false,
  children,
}: {
  textWidth?: string;
  flip?: boolean;
  children?: React.ReactNode;
}) => (
  <HStack
    spacing="14px"
    p="5px"
    borderRadius="full"
    boxShadow="md"
    bg="white"
    border="3px solid"
    borderColor="gray.100"
    minWidth="160px"
    justify="flex-start"
    flexDirection={flip ? 'row-reverse' : 'row'}
    pl={flip ? '24px' : '5px'}
    pr={flip ? '5px' : '24px'}
  >
    <SkeletonCircle size="58px" startColor="gray.100" endColor="gray.300" />
    <VStack alignItems={'flex-start'}>
      <Skeleton height="16px" width={textWidth} startColor="gray.100" endColor="gray.300" borderRadius="6px" />
      {children}
    </VStack>
  </HStack>
);
const WholenessRingSkeleton = ({
  greenScoreDescription = 'Your Strengths',
  yellowScoreDescription = 'Developing Potential',
  redScoreDescription = 'Opportunities for Growth',
  titleShown = false,
}: Props) => {
  return (
    <VStack width="100%" gap="10px" align="center" borderRadius="20px" p={{ base: '24px', md: '0 40px' }}>
      {/* Title */}
      {titleShown && (
        <VStack gap={'10px'}>
          <Text variant="loraSectionTitle" textAlign="start">
            Wholeness Ring
          </Text>
          <Text variant={'urbanistMedium'} color={'text.mediumGray'}>
            This graph displays the total scores of your wholeness broken down per domain.
          </Text>
        </VStack>
      )}
      {/* Main Circular Visualization */}
      <Box position="relative" w="full" maxW="600px" aspectRatio={1}>
        {/* Central ring */}
        <VStack position="absolute" top="50%" left="50%" transform="translate(-50%, -50%)" spacing="3">
          <SkeletonCircle size="180px" startColor="gray.100" endColor="gray.200" />
        </VStack>

        {/* Outer rings */}
        {Array.from({ length: 4 }).map((_, i) => (
          <Box
            key={i}
            position="absolute"
            top={`${8 + i * 7}%`}
            left={`${8 + i * 7}%`}
            right={`${8 + i * 7}%`}
            bottom={`${8 + i * 7}%`}
            border="2px solid"
            borderColor="gray.100"
            borderRadius="full"
          />
        ))}

        {/* Category Cards - approximate radial layout */}

        {/* Top-left */}
        <Box position="absolute" top="7%" left="15%">
          <CategoryCardSkeleton flip />
        </Box>

        {/* Top-right */}
        <Box position="absolute" top="7%" right="15%">
          <CategoryCardSkeleton textWidth="70px">
            <Skeleton height="16px" width={'72px'} startColor="gray.100" endColor="gray.300" borderRadius={'6px'} />
          </CategoryCardSkeleton>
        </Box>

        {/* Middle-left */}
        <Box position="absolute" top="30%" left="-5%">
          <CategoryCardSkeleton flip />
        </Box>

        {/* Middle-left */}
        <Box position="absolute" top="30%" right="-5%">
          <CategoryCardSkeleton textWidth="40px">
            <Skeleton height="16px" width={'60px'} startColor="gray.100" endColor="gray.300" borderRadius={'6px'} />
          </CategoryCardSkeleton>
        </Box>

        {/* Middle-left */}
        <Box position="absolute" top="60%" left="-5%" >
          <CategoryCardSkeleton flip />
        </Box>

        {/* Middle-right */}
        <Box position="absolute" top="60%" right="-5%">
          <CategoryCardSkeleton textWidth="72px">
            <Skeleton height="16px" width={'60px'} startColor="gray.100" endColor="gray.300" borderRadius={'6px'} />
          </CategoryCardSkeleton>
        </Box>

        {/* Bottom */}
        <Box position="absolute" bottom="7%" left="50%" transform="translateX(-50%)">
          <CategoryCardSkeleton />
        </Box>
      </Box>

      {/* Legend */}
      <ColoredDotsDescription
        dots={[
          { color: 'extra.teal', description: greenScoreDescription },
          { color: 'extra.yellowStrong', description: yellowScoreDescription },
          { color: 'extra.red', description: redScoreDescription },
        ]}
      />
    </VStack>
  );
};

export default WholenessRingSkeleton;
